import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Clock, 
  Flag, 
  FlagOff,
  Save, 
  Send,
  CheckCircle,
  Loader2,
  User,
  ArrowLeft,
  Copy,
  Check,
  AlertTriangle,
  FileText,
  ChevronLeft,
  ChevronRight,
  MessageSquare
} from 'lucide-react';

export default function FacilitatedSession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Session state
  const [session, setSession] = useState(null);
  const [participant, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Current question state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userResponse, setUserResponse] = useState('');
  const [currentNotes, setCurrentNotes] = useState('');
  const [isFlagged, setIsFlagged] = useState(false);
  
  // Session notes
  const [sessionNotes, setSessionNotes] = useState('');
  const [sessionNotesLastSaved, setSessionNotesLastSaved] = useState(null);
  
  // Timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const timerRef = useRef(null);
  
  // Auto-save
  const autoSaveTimeoutRef = useRef(null);
  const [saving, setSaving] = useState(false);
  
  // Results
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Escalation
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalatePriority, setEscalatePriority] = useState('normal');
  const [escalating, setEscalating] = useState(false);
  
  // Timestamped notes
  const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteType, setNewNoteType] = useState('general');
  const [addingNote, setAddingNote] = useState(false);
  
  // Fetch session data
  useEffect(() => {
    fetchSession();
    
    // Start timer
    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, [sessionId]);
  
  const fetchSession = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/facilitator/sessions/${sessionId}`);
      
      setSession(response.data.session);
      setParticipant(response.data.participant);
      setSessionNotes(response.data.session.sessionNotes || '');
      
      // Calculate elapsed time from session start
      if (response.data.session.startedAt) {
        const startTime = new Date(response.data.session.startedAt);
        const now = new Date();
        setElapsedSeconds(Math.floor((now - startTime) / 1000));
      }
      
      // If session is completed, show results
      if (response.data.session.status === 'completed') {
        setShowResults(true);
        setResults(response.data.session.results);
      } else {
        // Set current question index to the latest
        const assistantMessages = response.data.session.conversationLog?.filter(
          m => m.role === 'assistant'
        ) || [];
        setCurrentQuestionIndex(assistantMessages.length - 1);
      }
    } catch (error) {
      toast.error('Failed to load session');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  
  // Get questions and answers from conversation log
  const getConversationPairs = useCallback(() => {
    if (!session?.conversationLog) return [];
    
    const pairs = [];
    const messages = session.conversationLog.filter(m => m.role !== 'system');
    
    let questionIndex = 0;
    for (let i = 0; i < messages.length; i++) {
      if (messages[i].role === 'assistant') {
        const answer = messages[i + 1];
        pairs.push({
          index: questionIndex,
          question: messages[i].content,
          answer: answer?.content || null,
          notes: answer?.facilitatorNotes || '',
          flagged: answer?.flaggedForFollowUp || false,
          timestamp: messages[i].timestamp
        });
        questionIndex++;
        if (answer) i++; // Skip the answer in next iteration
      }
    }
    
    return pairs;
  }, [session?.conversationLog]);
  
  const conversationPairs = getConversationPairs();
  const currentPair = conversationPairs[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === conversationPairs.length - 1;
  const hasUnansweredQuestion = currentPair && !currentPair.answer;
  
  // Format timer display
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Auto-save session notes
  useEffect(() => {
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      if (sessionNotes !== session?.sessionNotes) {
        saveSessionNotes();
      }
    }, 2000);
    
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, [sessionNotes]);
  
  const saveSessionNotes = async () => {
    try {
      setSaving(true);
      await axios.put(`/api/facilitator/sessions/${sessionId}/session-notes`, {
        notes: sessionNotes
      });
      setSessionNotesLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save session notes:', error);
    } finally {
      setSaving(false);
    }
  };
  
  // Submit answer
  const submitAnswer = async () => {
    if (!userResponse.trim()) {
      toast.error('Please enter the participant\'s response');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`/api/facilitator/sessions/${sessionId}/respond`, {
        response: userResponse,
        facilitatorNotes: currentNotes,
        flagForFollowUp: isFlagged
      });
      
      // Clear form
      setUserResponse('');
      setCurrentNotes('');
      setIsFlagged(false);
      
      if (response.data.isComplete) {
        setShowResults(true);
        setResults(response.data.results);
        if (timerRef.current) clearInterval(timerRef.current);
        toast.success('Assessment completed!');
      } else {
        // Refresh session to get new question
        await fetchSession();
        toast.success('Response recorded');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Toggle flag for current question
  const toggleFlag = async () => {
    const newFlagState = !isFlagged;
    setIsFlagged(newFlagState);
    
    try {
      await axios.put(`/api/facilitator/sessions/${sessionId}/flag`, {
        questionIndex: currentQuestionIndex,
        flagged: newFlagState,
        flagReason: currentNotes
      });
    } catch (error) {
      console.error('Failed to update flag:', error);
    }
  };
  
  // Copy results summary
  const copyResultsSummary = async () => {
    try {
      const response = await axios.get(`/api/facilitator/sessions/${sessionId}/summary`);
      await navigator.clipboard.writeText(response.data.summaryText);
      setCopied(true);
      toast.success('Summary copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy summary');
    }
  };

  // Escalate session to admin
  const handleEscalate = async () => {
    if (!escalateReason.trim()) {
      toast.error('Please provide a reason for escalation');
      return;
    }
    
    setEscalating(true);
    try {
      await axios.post(`/api/facilitator/sessions/${sessionId}/escalate`, {
        reason: escalateReason,
        priority: escalatePriority
      });
      toast.success('Session escalated to admin for review');
      setShowEscalateDialog(false);
      setEscalateReason('');
      setEscalatePriority('normal');
      await fetchSession();
    } catch (error) {
      toast.error('Failed to escalate session');
    } finally {
      setEscalating(false);
    }
  };

  // Add timestamped note
  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    setAddingNote(true);
    try {
      await axios.post(`/api/facilitator/sessions/${sessionId}/notes`, {
        content: newNoteContent,
        noteType: newNoteType
      });
      toast.success('Note added');
      setShowAddNoteDialog(false);
      setNewNoteContent('');
      setNewNoteType('general');
      await fetchSession();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Results view
  if (showResults && results) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="container-custom py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/facilitator')}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
                </Button>
                <div className="h-6 w-px bg-border" />
                <div>
                  <h1 className="font-semibold">Session Complete</h1>
                  <p className="text-sm text-muted-foreground">
                    {participant?.firstName} {participant?.lastName} • {session?.type === 'fear' ? 'EDGE Diagnostic' : session?.type === 'archetype' ? 'CORE Diagnostic' : `${session?.type} Assessment`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Badge variant="secondary" className="gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(elapsedSeconds)}
                </Badge>
                <Button onClick={copyResultsSummary} data-testid="copy-summary-btn">
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Summary
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </header>
        
        <main className="container-custom py-8 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Results Card */}
            <Card className="border-success/50 bg-success/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-success">
                  <CheckCircle className="w-5 h-5" />
                  Assessment Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {session?.type === 'fear' ? (
                    <>
                      <div>
                        <label className="text-sm text-muted-foreground">Primary Edge</label>
                        <p className="text-lg font-semibold">{results.primary_fear_cluster || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Intensity</label>
                        <p className="text-lg font-semibold capitalize">{results.primary_fear_intensity || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Secondary Edge</label>
                        <p className="text-lg font-semibold">{results.secondary_fear_cluster || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Surface vs Root</label>
                        <p className="text-lg font-semibold">{results.surface_fear || 'N/A'} → {results.root_fear || 'N/A'}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm text-muted-foreground">Stated Archetype</label>
                        <p className="text-lg font-semibold">{results.primary_archetype || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Core Archetype</label>
                        <p className="text-lg font-semibold">{results.secondary_archetype || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm text-muted-foreground">Leadership Style</label>
                        <p className="text-lg font-semibold">{results.leadership_style || 'N/A'}</p>
                      </div>
                    </>
                  )}
                </div>
                
                {results.key_observations && results.key_observations.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-border">
                    <label className="text-sm text-muted-foreground block mb-2">Key Observations</label>
                    <ul className="space-y-2">
                      {results.key_observations.map((obs, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-primary">•</span>
                          <span>{obs}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Flagged Questions */}
            {session?.flaggedQuestions && session.flaggedQuestions.length > 0 && (
              <Card className="border-warning/50 bg-warning/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-warning">
                    <Flag className="w-5 h-5" />
                    Flagged for Follow-up ({session.flaggedQuestions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {session.flaggedQuestions.map((flag, idx) => {
                      const pair = conversationPairs[flag.questionIndex];
                      return (
                        <div key={idx} className="p-4 bg-card border border-border rounded-lg">
                          <div className="flex items-start gap-2 mb-2">
                            <Badge variant="outline">Q{flag.questionIndex + 1}</Badge>
                            <p className="text-sm">{pair?.question?.slice(0, 150)}...</p>
                          </div>
                          {flag.notes && (
                            <p className="text-sm text-muted-foreground ml-8">
                              Note: {flag.notes}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Session Notes */}
            {sessionNotes && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Session Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="whitespace-pre-wrap">{sessionNotes}</p>
                </CardContent>
              </Card>
            )}
            
            {/* Action Buttons */}
            <div className="flex justify-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/facilitator')}>
                Back to Dashboard
              </Button>
              <Button onClick={() => navigate(`/results/${session?.type}/${sessionId}`)}>
                View Full Results Page
              </Button>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }
  
  // Main facilitation view with split screen
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="facilitated-session">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/facilitator')}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Exit
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h1 className="font-semibold">
                    {participant?.firstName} {participant?.lastName}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {session?.type === 'fear' ? 'EDGE Diagnostic' : session?.type === 'archetype' ? 'CORE Diagnostic' : `${session?.type} Assessment`}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Timer */}
              <Badge variant="secondary" className="gap-1 text-base px-3 py-1" data-testid="session-timer">
                <Clock className="w-4 h-4" />
                {formatTime(elapsedSeconds)}
              </Badge>
              
              {/* Progress */}
              <Badge variant="outline" className="text-base px-3 py-1">
                Q{currentQuestionIndex + 1} of ~{session?.type === 'fear' ? '15' : '22'}
              </Badge>
              
              {/* Escalation indicator */}
              {session?.needsAdminReview && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Escalated
                </Badge>
              )}
              
              {/* Add Note Button */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowAddNoteDialog(true)}
                data-testid="add-note-btn"
              >
                <MessageSquare className="w-4 h-4 mr-1" />
                Add Note
              </Button>
              
              {/* Escalate Button */}
              {!session?.needsAdminReview && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowEscalateDialog(true)}
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                  data-testid="escalate-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Escalate to Admin
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content - Split Screen */}
      <main className="flex-1 flex">
        {/* Left Panel - Questions */}
        <div className="flex-1 border-r border-border overflow-hidden flex flex-col">
          {/* Question Navigation */}
          <div className="px-6 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={currentQuestionIndex === 0}
                onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium">
                Question {currentQuestionIndex + 1}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={currentQuestionIndex >= conversationPairs.length - 1}
                onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            
            {currentPair?.flagged && (
              <Badge variant="destructive" className="gap-1">
                <Flag className="w-3 h-3" />
                Flagged
              </Badge>
            )}
          </div>
          
          {/* Current Question */}
          <div className="flex-1 overflow-y-auto p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestionIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Question Card */}
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <Badge variant="outline" className="mb-2">Read aloud</Badge>
                        <p className="text-lg leading-relaxed whitespace-pre-wrap" data-testid="current-question">
                          {currentPair?.question || 'Loading question...'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Previous Answer (if exists and navigating back) */}
                {currentPair?.answer && !hasUnansweredQuestion && (
                  <Card className="bg-muted/30">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div>
                          <Badge variant="secondary" className="mb-2">Participant's Response</Badge>
                          <p className="text-muted-foreground whitespace-pre-wrap">
                            {currentPair.answer}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Answer Input (only for current unanswered question) */}
                {hasUnansweredQuestion && isLastQuestion && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium mb-2 block">
                        Record Participant's Response
                      </label>
                      <Textarea
                        value={userResponse}
                        onChange={(e) => setUserResponse(e.target.value)}
                        placeholder="Type exactly what the participant says..."
                        className="min-h-[120px] text-base"
                        data-testid="response-input"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Button
                        variant={isFlagged ? "destructive" : "outline"}
                        size="sm"
                        onClick={toggleFlag}
                        data-testid="flag-btn"
                      >
                        {isFlagged ? (
                          <>
                            <FlagOff className="w-4 h-4 mr-1" />
                            Unflag
                          </>
                        ) : (
                          <>
                            <Flag className="w-4 h-4 mr-1" />
                            Flag for Follow-up
                          </>
                        )}
                      </Button>
                      
                      <Button 
                        onClick={submitAnswer} 
                        disabled={submitting || !userResponse.trim()}
                        data-testid="submit-answer-btn"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Submit & Next Question
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        
        {/* Right Panel - Notes */}
        <div className="w-[400px] flex flex-col bg-muted/20">
          <div className="px-6 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
            <h3 className="font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Session Notes
            </h3>
            {saving ? (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </span>
            ) : sessionNotesLastSaved && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Check className="w-3 h-3 text-success" />
                Saved
              </span>
            )}
          </div>
          
          <div className="flex-1 p-4 space-y-4 overflow-y-auto">
            {/* Question-specific notes */}
            {hasUnansweredQuestion && isLastQuestion && (
              <div>
                <label className="text-sm font-medium mb-2 block text-muted-foreground">
                  Notes for Q{currentQuestionIndex + 1}
                </label>
                <Textarea
                  value={currentNotes}
                  onChange={(e) => setCurrentNotes(e.target.value)}
                  placeholder="Observations about this question..."
                  className="min-h-[100px] text-sm bg-card"
                  data-testid="question-notes"
                />
              </div>
            )}
            
            {/* Overall session notes */}
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block text-muted-foreground">
                Overall Session Notes
              </label>
              <Textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                placeholder="General observations, body language, tone, key moments..."
                className="min-h-[200px] text-sm bg-card"
                data-testid="session-notes"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Auto-saves every 2 seconds
              </p>
            </div>
            
            {/* Flagged items summary */}
            {session?.flaggedQuestions && session.flaggedQuestions.length > 0 && (
              <Card className="bg-warning/10 border-warning/30">
                <CardHeader className="py-3">
                  <CardTitle className="text-sm flex items-center gap-2 text-warning">
                    <Flag className="w-4 h-4" />
                    Flagged ({session.flaggedQuestions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <ul className="text-sm space-y-1">
                    {session.flaggedQuestions.map((flag, idx) => (
                      <li key={idx} className="text-muted-foreground">
                        • Question {flag.questionIndex + 1}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      
      {/* Escalate to Admin Dialog */}
      <Dialog open={showEscalateDialog} onOpenChange={setShowEscalateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Escalate Session to Admin
            </DialogTitle>
            <DialogDescription>
              Flag this session for admin review. The admin will be notified and can follow up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason for Escalation</label>
              <Textarea
                placeholder="Describe why this session needs admin attention..."
                value={escalateReason}
                onChange={(e) => setEscalateReason(e.target.value)}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Priority</label>
              <div className="flex gap-2">
                {['low', 'normal', 'high', 'urgent'].map((p) => (
                  <Button
                    key={p}
                    variant={escalatePriority === p ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEscalatePriority(p)}
                    className={escalatePriority === p ? '' : 'text-muted-foreground'}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEscalate} 
              disabled={escalating || !escalateReason.trim()}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {escalating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Escalating...
                </>
              ) : (
                'Escalate Session'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Timestamped Note Dialog */}
      <Dialog open={showAddNoteDialog} onOpenChange={setShowAddNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Add Session Note
            </DialogTitle>
            <DialogDescription>
              Add a timestamped note to this session. Notes are visible to admins reviewing the session.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Note Type</label>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 'general', label: 'General', color: 'bg-gray-100' },
                  { value: 'observation', label: 'Observation', color: 'bg-blue-100' },
                  { value: 'concern', label: 'Concern', color: 'bg-amber-100' },
                  { value: 'action_item', label: 'Action Item', color: 'bg-green-100' }
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={newNoteType === type.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewNoteType(type.value)}
                  >
                    {type.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Note Content</label>
              <Textarea
                placeholder="Enter your note..."
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNoteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddNote} 
              disabled={addingNote || !newNoteContent.trim()}
            >
              {addingNote ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Note'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
